import { useCallback, useState } from 'react';

import {
  makeApiError,
  makeApiSuccess,
  makeApiLimitReached,
} from 'scenes/common/helpers/apiData';
import { ApiState } from 'scenes/common/types';
import { useFunnelState } from 'scenes/common/contexts/quoteFlowContext';

import analytics from 'scenes/motor/analytics';
import {
  MotorStepType,
  MotorFormState,
  MotorFunnelState,
  VehicleSearchType,
  MotorApiResponses,
  ReferenceData,
} from 'scenes/motor/types';
import api from 'scenes/motor/api/regoSearch';

type RegoSearchFunction = (licencePlate: string) => void;

const REGO_NOT_FOUND_MAX_COUNT = 3;

export const useRegoSearch = (
  referenceData: ReferenceData,
  onApiResponse: (res: Partial<MotorApiResponses>) => void,
  onNextIfLocationIs: (step: MotorStepType, values: MotorFormState) => void
): RegoSearchFunction => {
  const funnelState = useFunnelState<MotorFunnelState>();
  const { quoteDetails, step } = funnelState;
  const [regoNotFoundCount, setRegoNotFoundCount] = useState(0);
  const residentialState = quoteDetails.overnightParkingAddress
    ?.state as string;

  return useCallback<RegoSearchFunction>(
    (licencePlate) => {
      onApiResponse({
        foundVehicle: { state: ApiState.LoadingWithNoData },
      });
      api()
        .getVehicleByRego(licencePlate, residentialState)
        .then(
          (data) => {
            if (!data) {
              const newRegoNotFoundCount = regoNotFoundCount + 1;

              if (newRegoNotFoundCount >= REGO_NOT_FOUND_MAX_COUNT) {
                onApiResponse({
                  foundVehicle: makeApiLimitReached(
                    'Sorry, we cannot find the car, and you have reached the maximum number of registration searches. Please search for your car manually.'
                  ),
                });
              } else {
                analytics().carNotFound(funnelState);
                onApiResponse({
                  foundVehicle: makeApiError(
                    'Sorry, we cannot find the car. Please enter the registration again or search manually.'
                  ),
                });
                setRegoNotFoundCount(newRegoNotFoundCount);
              }
            } else {
              onApiResponse({
                foundVehicle: makeApiSuccess(data),
              });
              analytics().findCar(funnelState, data, referenceData);
              onNextIfLocationIs(step, {
                licencePlate,
                vehicleSearchType: VehicleSearchType.RegoLookup,
                vehicleFromSearch: data,
              });
            }
          },
          (err) => {
            analytics().error(funnelState, err.message);
            onApiResponse({
              foundVehicle: makeApiError(err.message),
            });
          }
        );
    },
    [
      step,
      funnelState,
      onApiResponse,
      referenceData,
      residentialState,
      regoNotFoundCount,
      onNextIfLocationIs,
    ]
  );
};
